/**
 * A search field whose filters are chips: chips, then the text being typed, wrapping as they grow. The chips come from
 * the screen (Mail: logic/mailQuery); a chip's press opens its `menu` (how it matches), its × removes it, and Backspace
 * on the empty field goes to `onKey`. Keys the menu needs (↑ ↓ Enter Tab Esc) go to `onKey` too.
 */
import type { ChangeEvent, KeyboardEvent, SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { Chip, type ChipTone } from "./Chip.tsx";
import { Icon } from "./Icon.tsx";
import { Menu } from "./Menu.tsx";
import { Text } from "./Text.tsx";
import { cx } from "./tokens.ts";
import type { MenuEntry } from "./types.ts";

export type SearchChipFieldKey = "up" | "down" | "enter" | "tab" | "escape" | "backspace";

export type SearchChipItem = {
  id: string;
  label: string;
  tone?: ChipTone;
  /** Pressing the chip does this (it has no `menu`). */
  onPress?: () => void;
  /** Its name when pressing it isn't obvious from the label. */
  accessibilityLabel?: string;
  /** Drawn before the chip ("or"). */
  joiner?: string;
  menu?: MenuEntry[];
  removeLabel: string;
  onRemove: () => void;
};

export type SearchChipFieldProps = {
  chips: SearchChipItem[];
  value: string;
  onChange: (text: string) => void;
  onCaret?: (caret: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** "search" (default) has a magnifier and a border; "plain" neither, for the mail composer's lines. */
  variant?: "search" | "plain";
  keyboard?: "default" | "email";
  autoFocus?: boolean;
  /** Puts the keyboard in the field whenever it changes. */
  focusSeq?: number;
  /** Return true when the key was used: the field then does nothing with it. */
  onKey?: (key: SearchChipFieldKey) => boolean;
  placeholder: string;
  accessibilityLabel: string;
  testID: string;
};

const KEYS: Partial<Record<string, SearchChipFieldKey>> = { ArrowUp: "up", ArrowDown: "down", Enter: "enter", Tab: "tab", Escape: "escape", Backspace: "backspace" };

export function SearchChipField({ chips, value, onChange, onCaret, onFocus, onBlur, variant = "search", keyboard = "default", autoFocus, focusSeq, onKey, placeholder, accessibilityLabel, testID }: SearchChipFieldProps) {
  const [open, setOpen] = useState<string>();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusSeq !== undefined && focusSeq > 0) input.current?.focus();
  }, [focusSeq]);
  const caret = (e: SyntheticEvent<HTMLInputElement>) => onCaret?.(e.currentTarget.selectionEnd ?? e.currentTarget.value.length);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = KEYS[e.key];
    if (key === undefined || e.nativeEvent.isComposing || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
    if (onKey?.(key) === true) e.preventDefault();
  };
  return (
    <div className={cx("b-field b-chip-field", variant === "plain" ? "b-field-plain" : "b-field-outlined")}>
      <div className="b-field-box">
        {variant === "plain" ? null : <Icon name="search" size="icon.sm" color="text.tertiary" />}
        {chips.map((c) => (
          <span key={c.id} className="b-chip-field-item">
            {c.joiner === undefined ? null : (
              <Text variant="caption" weight="semibold" color="text.tertiary">
                {c.joiner}
              </Text>
            )}
            <Chip
              label={c.label}
              {...(c.tone === undefined ? {} : { tone: c.tone })}
              {...(c.menu === undefined ? (c.onPress === undefined ? {} : { onPress: c.onPress }) : { onPress: () => setOpen(c.id) })}
              {...(c.accessibilityLabel === undefined ? {} : { accessibilityLabel: c.accessibilityLabel })}
              onRemove={c.onRemove}
              removeLabel={c.removeLabel}
              testID={`${testID}-chip-${c.id}`}
            />
            {c.menu === undefined ? null : (
              <Menu items={c.menu} open={open === c.id} onOpenChange={(o) => setOpen(o ? c.id : undefined)} testID={`${testID}-chip-${c.id}-menu`}>
                <span className="b-chip-field-anchor" aria-hidden="true" />
              </Menu>
            )}
          </span>
        ))}
        <input
          ref={input}
          className="b-field-input"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
            caret(e);
          }}
          onSelect={caret}
          onFocus={onFocus}
          onClick={onFocus}
          onBlur={onBlur}
          inputMode={keyboard === "email" ? "email" : undefined}
          autoCapitalize={keyboard === "email" ? "none" : undefined}
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          placeholder={chips.length === 0 ? placeholder : undefined}
          aria-label={accessibilityLabel}
          spellCheck={false}
          autoComplete="off"
          data-testid={testID}
        />
      </div>
    </div>
  );
}
